<script setup lang="ts">
    import { computed, reactive, onMounted } from 'vue';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import { getSentIssueStatistics } from './Issues.access';
    import { type IssueStatistics } from './interfaces';
    import { timeout } from './Issues';

    const emit = defineEmits<{
        (ev: 'pauseRunningIssue'): void;
        (ev: 'pauseIssue'): void;
        (ev: 'isSent'): void;
    }>();

    const { issueId, loading } = defineProps<{
        issueId: string;
        loading: boolean;
    }>();

    const state = reactive<{
        issueStatistics: IssueStatistics | null;
        isPausing: boolean;
        loading: boolean;
    }>({
        issueStatistics: null,
        isPausing: false,
        loading: true,
    });

    onMounted(async () => {
        updateIssueStatistics();
    });

    const updateIssueStatistics = async () => {
        state.issueStatistics = await getSentIssueStatistics(issueId);
        state.loading = false;

        if (isSent.value) {
            return emit('isSent');
        }

        setTimeout(() => {
            updateIssueStatistics();
        }, getTimeout.value);
    };

    const isSent = computed(() => {
        if (!state.issueStatistics) return false;
        return state.issueStatistics.Ended && state.issueStatistics.Ended > state.issueStatistics.Started;
    });

    const getSentCount = computed(() => {
        if (!state.issueStatistics) return 0;
        return state.issueStatistics.SentCount + state.issueStatistics.FailedCount;
    });

    const getSentPercentage = computed(() => {
        if (!state.issueStatistics) return 0;
        return state.issueStatistics.RecipientCount === 0 ? 0 : (getSentCount.value / state.issueStatistics.RecipientCount) * 100;
    });

    const isRunningIssue = computed(() => {
        if (!state.issueStatistics) return false;
        return getSentCount.value && state.issueStatistics.RecipientCount > 1000 && getSentCount.value < state.issueStatistics.RecipientCount;
    });

    const getTimeout = computed(() => {
        if (!state.issueStatistics) return timeout;
        return state.issueStatistics.RecipientCount > 1000 ? timeout * 2 : timeout;
    });

    const pauseRunningIssue = () => {
        state.isPausing = true;
        emit('pauseRunningIssue');
    };
</script>

<template>
    <template v-if="state.issueStatistics">
        <div class="align-self-center d-flex flex-wrap gap-sm align-items-center m-xs-b">
            <div class="ug-loading-center"></div>

            {{ !getSentCount ? $Resources.MailingPreparation : isSent ? $Resources.IssuesSendingLabel : '' }}

            <button v-if="isRunningIssue" type="button" class="btn btn-default btn-sm" :disabled="loading" @click="pauseRunningIssue">
                {{ $Resources.IssuesAbortSendingButton }}
            </button>

            <button
                v-if="!getSentCount"
                type="button"
                class="btn btn-default btn-sm"
                :class="{ 'is-busy': state.isPausing }"
                :disabled="loading"
                @click="$emit('pauseIssue')">
                {{ $Resources.IssuesAbortSendingButton }}
            </button>
        </div>

        <div class="align-self-center">
            <div class="ug-bars">
                <div class="bar-label">
                    {{ $Resources.SentLabel }}
                    <span class="skeleton" :class="state.loading && 'loading'">
                        {{
                            formatNumericText(getSentCount, {
                                precision: 1,
                                largeNumbers: true,
                            }) +
                            '/' +
                            formatNumericText(state.issueStatistics.RecipientCount, { precision: 1, largeNumbers: true })
                        }}
                    </span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-sent" :style="{ width: getSentPercentage + '%' }"></span>
                </div>
            </div>
        </div>
    </template>
</template>
